import React, { memo, useCallback, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Trash2 } from 'lucide-react';

export type DeleteModalProps = {
  isOpen: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  cancelLabel?: string;
  isConfirming?: boolean;
};

export const DeleteModal = ({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  isConfirming = false,
}: DeleteModalProps) => {
  const titleId = useId();
  const descId = useId();

  const stopPanelClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || isConfirming || typeof window === 'undefined') return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isConfirming, handleCancel]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] grid place-items-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="absolute inset-0" aria-hidden="true" onClick={handleCancel} />
      <div
        className="relative z-10 flex w-full max-w-md max-h-[min(90vh,40rem)] flex-col overflow-hidden rounded-xl border-2 border-red-200 bg-white shadow-2xl"
        onClick={stopPanelClick}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-red-100 bg-red-50 px-5 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <Trash2 className="h-5 w-5 text-red-600" aria-hidden />
          </div>
          <div className="min-w-0 pt-0.5">
            <h2 id={titleId} className="text-lg font-semibold text-red-900">
              {title}
            </h2>
          </div>
        </div>
        <div id={descId} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <p className="whitespace-pre-line text-sm leading-relaxed text-red-950/85">{message}</p>
        </div>
        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            disabled={isConfirming}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isConfirming}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default memo(DeleteModal);